<template>
  <div class="reviews-page">
    <!-- Interactive patient-view preview (full-page split; replaces the tabs,
         mirrors the design's `isPatient` screen). Own "Back to reviews" link. -->
    <ReviewsPatientPreview v-if="previewing" @back="previewing = false" />

    <template v-else>
      <!-- Sticky header -->
      <div class="rv-header">
        <div class="rv-header-top">
          <div class="rv-header-titles">
            <div class="rv-title-row">
              <h1 class="rv-title">Reviews</h1>
              <span class="rv-live-pill">
                <span class="rv-live-dot"></span>Live · synced with Open Dental
              </span>
            </div>
            <div class="rv-subtitle">Automatic review requests and private feedback, straight from your schedule.</div>
          </div>
          <div class="rv-header-actions">
            <button class="rv-hbtn rv-hbtn-secondary" @click="previewing = true">Preview patient view</button>
            <button class="rv-hbtn rv-hbtn-primary" @click="setTab('settings')">Automation settings</button>
          </div>
        </div>

        <!-- Underline tabs -->
        <div class="rv-tabs">
          <button
            v-for="t in tabs"
            :key="t.key"
            class="rv-tab"
            :class="{ active: activeTab === t.key }"
            @click="setTab(t.key)"
          >
            {{ t.label }}
          </button>
        </div>
      </div>

      <!-- Tab body -->
      <div class="rv-body">
        <ReviewsRequests v-if="activeTab === 'requests'" />
        <ReviewsUpcoming v-else-if="activeTab === 'upcoming'" />
        <ReviewsDoNotSend v-else-if="activeTab === 'do-not-send'" />
        <ReviewsPrivateFeedback v-else-if="activeTab === 'feedback'" />
        <ReviewsSettings v-else-if="activeTab === 'settings'" />
      </div>
    </template>
  </div>
</template>

<script>
/* eslint-disable */
import mixin from '../../mixin.js';
import ReviewsRequests from './components/ReviewsRequests';
import ReviewsUpcoming from './components/ReviewsUpcoming';
import ReviewsDoNotSend from './components/ReviewsDoNotSend';
import ReviewsPrivateFeedback from './components/ReviewsPrivateFeedback';
import ReviewsSettings from './components/ReviewsSettings';
import ReviewsPatientPreview from './components/ReviewsPatientPreview';

// 'overview' is deliberately ABSENT. That tab's only unique content was Phase B
// placeholders — a four-point chart, pills filtering a Google review list that does not
// exist yet, and a "no Google data" rating panel — so it showed an office nothing it could
// act on. Removed 2026-08-12; recover the markup from commit b2410cce when building B5.
// 'upcoming' (skip a visit before it happens) and 'do-not-send' (the visible opt-out list)
// added 2026-09-14.
const VALID_TABS = ['requests', 'upcoming', 'do-not-send', 'feedback', 'settings'];

// Still accepted on the way IN so old bookmarks and any ?tab=overview link land on the
// working surface instead of falling through to an empty body.
const RETIRED_TABS = { overview: 'requests' };

export default {
  name: 'Reviews',
  components: { ReviewsRequests, ReviewsUpcoming, ReviewsDoNotSend, ReviewsPrivateFeedback, ReviewsSettings, ReviewsPatientPreview },
  mixins: [mixin],

  data() {
    return {
      previewing: false,
      tabs: [
        { key: 'requests', label: 'Requests' },
        { key: 'upcoming', label: 'Upcoming' },
        { key: 'do-not-send', label: 'Do not send' },
        { key: 'feedback', label: 'Private feedback' },
        { key: 'settings', label: 'Settings' },
      ],
    };
  },

  computed: {
    // The tab is derived from ?tab= so deep links work: the alert email opens
    // ?tab=feedback and the "Automation settings" button opens ?tab=settings.
    activeTab() {
      const t = this.$route.query.tab;
      if (VALID_TABS.includes(t)) return t;
      return RETIRED_TABS[t] || 'requests';
    },
  },

  methods: {
    setTab(key) {
      if (!VALID_TABS.includes(key) || key === this.activeTab) return;
      // replace() (not push) so tab switching doesn't spam browser history.
      this.$router.replace({ query: { ...this.$route.query, tab: key } }).catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
$primary: #0f766e;
$border: #e2e8f0;
$neutral-500: #64748b;
$neutral-900: #0f172a;

.reviews-page { max-width: 100%; }

.rv-header {
  position: sticky;
  top: 0;
  z-index: 5;
  background: rgba(250, 251, 251, 0.9);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid $border;
  margin: -8px -8px 20px;
  padding: 14px 8px 0;
}
.rv-header-top { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.rv-title-row { display: flex; align-items: center; gap: 10px; }
.rv-title { font-size: 24px; font-weight: 800; letter-spacing: -0.02em; margin: 0; color: $neutral-900; }
.rv-live-pill {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600; color: #0d9488; background: #ecfdf5;
  padding: 3px 9px; border-radius: 999px;
}
.rv-live-dot { width: 7px; height: 7px; border-radius: 999px; background: #14b8a6; animation: rv-pulse 1.6s ease-in-out infinite; }
@keyframes rv-pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.35; transform: scale(0.7); } }
.rv-subtitle { color: $neutral-500; font-size: 13px; margin-top: 4px; }

.rv-header-actions { display: flex; gap: 10px; padding-bottom: 2px; }
.rv-hbtn { border: none; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 600; padding: 8px 16px; border-radius: 9px; transition: background 0.15s ease; }
.rv-hbtn-secondary { background: #fff; border: 1px solid #cbd5e1; color: #334155; &:hover { background: #f8fafc; } }
.rv-hbtn-primary { background: $primary; color: #fff; &:hover { background: #0b5f58; } }

/* Five tabs are wider than a phone: the strip scrolls sideways on its own (scrollbar hidden,
   as in UnreadFilterChips.vue) so the PAGE never does. */
.rv-tabs {
  display: flex; gap: 4px; margin-top: 14px;
  overflow-x: auto; -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
}
.rv-tab {
  white-space: nowrap; flex-shrink: 0;
  border: none; background: transparent; cursor: pointer; font-family: inherit;
  font-size: 13.5px; font-weight: 600; color: $neutral-500;
  padding: 10px 14px; border-bottom: 2px solid transparent; margin-bottom: -1px;
  &:hover { color: #334155; }
  &.active { color: $primary; border-bottom-color: $primary; }
}

.rv-body { padding: 4px 8px 40px; }
</style>
